import { Injectable } from '@angular/core';
import { createStore } from '@ngneat/elf';
import { selectEntity, upsertEntitiesById, withEntities } from '@ngneat/elf-entities';
import { createRequestsCacheOperator, updateRequestCache, withRequestsCache } from '@ngneat/elf-requests';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { FavoritesFileI } from '../models/favorites';
import { PublicationTypeE } from '../models/bookPartLink';

interface FavoritesStateI {
    userUuid: string;
    favorites: FavoritesFileI[];
}

const store = createStore(
    { name: 'favorites' },
    withEntities<FavoritesStateI, 'userUuid'>({ idKey: 'userUuid' }),
    withRequestsCache<`favorites-${string}`>()
);

export const skipWhileFavoritesCached = createRequestsCacheOperator(store);
export const CACHE_KEY_FAVORITES = 'favorites';

@Injectable({ providedIn: 'root' })
export class FavoritesRepository {
    selectFavorites$(userUuid: string): Observable<FavoritesFileI[]> {
        return store.pipe(selectEntity(userUuid, { pluck: 'favorites' }));
    }

    selectFavoriteByPublication$(
        userUuid: string,
        publicationId: number,
        publicationType: PublicationTypeE
    ): Observable<FavoritesFileI | undefined> {
        return this.selectFavorites$(userUuid).pipe(
            map(favorites =>
                favorites?.find(
                    favorite => favorite.publicationId === publicationId && favorite.publicationType === publicationType
                )
            )
        );
    }

    setFavorites(userUuid: string, favorites: FavoritesFileI[]): void {
        store.update(
            updateRequestCache(`${CACHE_KEY_FAVORITES}-${userUuid}`),
            upsertEntitiesById(userUuid, {
                creator: () => ({ userUuid, favorites }),
                updater: entity => ({ ...entity, favorites })
            })
        );
    }

    addFavorite(userUuid: string, favorite: FavoritesFileI): void {
        store.update(
            upsertEntitiesById(userUuid, {
                creator: () => ({ userUuid, favorites: [favorite] }),
                updater: entity => ({ ...entity, favorites: [favorite, ...(entity.favorites || [])] })
            })
        );
    }

    updateFavorite(userUuid: string, updatedFavorite: FavoritesFileI): void {
        store.update(
            upsertEntitiesById(userUuid, {
                creator: () => ({ userUuid, favorites: [updatedFavorite] }),
                updater: entity => ({
                    ...entity,
                    favorites: (entity.favorites || []).map(favorite =>
                        favorite.id === updatedFavorite.id ? updatedFavorite : favorite
                    )
                })
            })
        );
    }

    removeFavorite(userUuid: string, favoritesFileId: number): void {
        store.update(
            upsertEntitiesById(userUuid, {
                creator: () => ({ userUuid, favorites: [] }),
                updater: entity => ({
                    ...entity,
                    favorites: (entity.favorites || []).filter(favorite => favorite.id !== favoritesFileId)
                })
            })
        );
    }

    clearCacheOfUser(userUuid: string): void {
        store.update(updateRequestCache(`${CACHE_KEY_FAVORITES}-${userUuid}`, { value: 'none' }));
    }
}
